import { LockOutlineBaseIcon } from '@tableau/icons/LockOutlineBaseIcon';

import { Set } from '~commonInfra/utils/Set';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { Messages } from '../../codegen/Localize';
import { ITypedIdWithName } from '../../httpClient/serverApi/types/ITypedId';
import { INamedItemInfo } from '../../spacesPlatform/models/resourceInfoParts';
import { IWorkbookInfo } from '../../workbook/models/IWorkbookInfo';
import { PermissionsLockIcon } from '../components/PermissionsLockUnlock';
import { AuthorizableType } from '../types/AuthorizableType';

const dbInProjectsEnabled: boolean =  FeatureFlags.databasesInProjectsEnabled && FeatureFlags.databasesInProjectsPostMVPEnabled;

export enum WorkbookSheetsAsTabsState {
  DisplayTabs,
  IndependentTabs,
  Mixed,
}

export interface PermissionsEditability {
  /**
   * Can the permissions of the given authorizables be edited?
   * If some can and some can't, the overall editability is false.
   */
  editable: boolean;

  /**
   * The string to render if authorizable type is not a container. For example,
   * when the parent project permissions are locked and the message might say "Locked to parent project".
   * For container authorizable types, strings are set in PermissionsLockUnlock.
   */
  nonContainerMessage: string;

  /**
   * The icon (if any) that corresponds with the editability
   */
  icon?: PermissionsLockIcon;

  /**  The string to render on a capability if the editability needs an extra explanation,
   * such as why a capability is not editable or why editing it might not have the desired behavior
   */
  editabilityDescription?: string;
}

export module PermissionsEditabilityLogic {
  /**
   * An authorizable object has a source authorizable object attached.
   * If the object is in control of its own permissions, it will be its own source.
   * If the object is controlled by another object (e.g a workbook inside a locked project),
   * then the source authorizable object will be the controlling object.
   * So permissions of object A are editable if and only if A matches sourceAuthorizables[A.id]
   */
  function allAuthorizablesMatchSource(
    authorizables: Set<INamedItemInfo>,
    authorizableType: string,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>
  ): boolean {
    return authorizables.all((authorizable: INamedItemInfo) => {
      const source = sourceAuthorizables[authorizable.id];
      return authorizableType === source.type && authorizable.id === source.id;
    });
  }

  function noAuthorizablesMatchSource(
    authorizables: Set<INamedItemInfo>,
    authorizableType: string,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>
  ): boolean {
    return authorizables.all((authorizable: INamedItemInfo) => {
      const source = sourceAuthorizables[authorizable.id];
      return authorizableType !== source.type || authorizable.id !== source.id;
    });
  }

  function getSheetsAsTabsState(authorizables: Set<INamedItemInfo>): WorkbookSheetsAsTabsState {
    const workbooks = <Set<IWorkbookInfo>>authorizables;
    const displayTabs = workbooks.first().displayTabs;
    const allTheSame = workbooks.all((workbook: IWorkbookInfo) => workbook.displayTabs === displayTabs);
    if (!allTheSame) {
      return WorkbookSheetsAsTabsState.Mixed;
    } else if (displayTabs) {
      return WorkbookSheetsAsTabsState.DisplayTabs;
    } else {
      return WorkbookSheetsAsTabsState.IndependentTabs;
    }
  }

  function getNumSourcedFromProject(
    authorizables: Set<INamedItemInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>
  ): number {
    return authorizables.map((authorizable: INamedItemInfo): number =>
      sourceAuthorizables[authorizable.id].type === AuthorizableType.PROJECT ? 1 : 0
    ).reduce((memo: number, next: number) => memo + next);
  }

  function getNumSourcedFromWorkbook(
    authorizables: Set<INamedItemInfo>,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>
  ): number {
    return authorizables.map((authorizable: INamedItemInfo): number =>
      sourceAuthorizables[authorizable.id].type === AuthorizableType.WORKBOOK ? 1 : 0
    ).reduce((memo: number, next: number) => memo + next);
  }

  function getEditable(
    authorizables: Set<INamedItemInfo>,
    authorizableType: AuthorizableType,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>
  ): boolean {
    switch (authorizableType) {
      case AuthorizableType.COLLECTION:
        // Collection is a top-level container where nesting is not supported,
        // so they will always be editable.
        return true;
      case AuthorizableType.DATABASE:
        if (!dbInProjectsEnabled) return true;  // When DBs in projects is enabled DBs are not always editable
      default:
        return allAuthorizablesMatchSource(authorizables, authorizableType, sourceAuthorizables);
    }
  }

  function getAllEditabilityData(
    editable: boolean,
    authorizables: Set<INamedItemInfo>,
    authorizableType: AuthorizableType,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>
  ): PermissionsEditability {
    let nonContainerMessage = '';
    switch (authorizableType) {
      case AuthorizableType.COLLECTION:
        // Although collection is a container, there needs to be a message to clarify that
        // the collection's permissions do not change permissions for items inside the collection.
        return {
          editable,
          nonContainerMessage: Messages.permissionsInfoMessageForCollection(),
        };

      case AuthorizableType.PROJECT:
        // Container authorizable types don't need a message or icon as part of editability logic.
        // Their strings are set in PermissionsLockUnlock.
        if (editable) {
          // all uncontrolled
          return {
            editable,
            nonContainerMessage,
          };
        } else if (noAuthorizablesMatchSource(authorizables, authorizableType, sourceAuthorizables)) {
          // all controlled children
          return {
            editable,
            nonContainerMessage,
            editabilityDescription: Messages.lockPermissions_capabilityTooltip_lockedProject(),
          };
        } else {
          // mixed
          return {
            editable,
            nonContainerMessage,
          };
        }

      case AuthorizableType.WORKBOOK:
        // Message for workbook has to take into account "sheet as tabs" state
        if (editable) {
          const sheetAsTabsState = getSheetsAsTabsState(authorizables);
          if (sheetAsTabsState === WorkbookSheetsAsTabsState.DisplayTabs) {
            nonContainerMessage = Messages.lockPermissions_workbookViewsDisplayTabs();
          } else if (sheetAsTabsState === WorkbookSheetsAsTabsState.IndependentTabs) {
            return {
              editable,
              nonContainerMessage: Messages.lockPermissions_workbookViewsIndependentTabs(),
              editabilityDescription: Messages.lockPermissions_capabilityTooltip_independentTabs(),
            };
          } else {
            nonContainerMessage = Messages.lockPermissions_workbookViewsMixedTabs();
          }
          return {
            editable,
            nonContainerMessage,
          };
        } else if (noAuthorizablesMatchSource(authorizables, authorizableType, sourceAuthorizables)) {
          return {
            editable,
            nonContainerMessage: Messages.lockPermissions_contentInLockedProject(),
            icon: LockOutlineBaseIcon,
            editabilityDescription: Messages.lockPermissions_capabilityTooltip_lockedProject(),
          };
        } else {
          return {
            editable,
            nonContainerMessage: Messages.lockPermissions_contentInMixedProject(),
          };
        }

      case AuthorizableType.VIEW:
        // Message for view has to take into account where exactly permissions are sourced
        // because permissions for a view could be controlled by workbook or project.
        // Only if everything is sourced from project, then the message should say "Locked to parent project".
        const numSourcedFromProject = getNumSourcedFromProject(authorizables, sourceAuthorizables);
        if (!editable && numSourcedFromProject > 0) {
          if (numSourcedFromProject === authorizables.size()) {
            return {
              editable,
              nonContainerMessage: Messages.lockPermissions_contentInLockedProject(),
              icon: LockOutlineBaseIcon,
              editabilityDescription: Messages.lockPermissions_capabilityTooltip_lockedProject(),
            };
          } else {
            return {
              editable,
              nonContainerMessage: Messages.lockPermissions_contentInMixedProject(),
            };
          }
        } else {
          const numSourcedFromWorkbook = getNumSourcedFromWorkbook(authorizables, sourceAuthorizables);
          if (numSourcedFromWorkbook > 0) {
            if (numSourcedFromWorkbook === authorizables.size()) {
              return {
                editable,
                nonContainerMessage: Messages.lockPermissions_workbookViewsDisplayTabs(),
                editabilityDescription: Messages.lockPermissions_capabilityTooltip_tabbedWorkbook(),
              };
            } else {
              return {
                editable,
                nonContainerMessage: Messages.lockPermissions_workbookViewsMixedTabs(),
              };
            }
          } else {
            return {
              editable,
              nonContainerMessage: Messages.lockPermissions_workbookViewsIndependentTabs(),
            };
          }
        }

      case AuthorizableType.TABLE:
        if (editable) {
          return {
            editable,
            nonContainerMessage,
          };
        } else if (noAuthorizablesMatchSource(authorizables, authorizableType, sourceAuthorizables)) {
          let isTableInProject = getSessionManagementServiceInstance().databasesInProjectsPostMVPEnabled() && sourceAuthorizables[authorizables.first().id].type === AuthorizableType.PROJECT;
          return {
            editable,
            nonContainerMessage: isTableInProject ? Messages.lockPermissions_contentInLockedProject() : Messages.lockPermissions_contentInLockedDatabase(),
            icon: LockOutlineBaseIcon,
            editabilityDescription: isTableInProject ? Messages.lockPermissions_capabilityTooltip_lockedProject() : Messages.lockPermissions_capabilityTooltip_lockedDatabase(),
          };
        } else {
          return {
            editable,
            nonContainerMessage: Messages.lockPermissions_contentInMixedDatabase() ,
          };
        }

      case AuthorizableType.DATABASE:
        if (!dbInProjectsEnabled) {
          // Container authorizable types don't need a message or icon as part of editability logic.
          // Their strings are set in PermissionsLockUnlock. Databases are only top level, so they do not need nonEditableReason.
          return {
            editable,
            nonContainerMessage,
          };
        }

      default:
        if (editable) {
          return {
            editable,
            nonContainerMessage,
          };
        } else if (noAuthorizablesMatchSource(authorizables, authorizableType, sourceAuthorizables)) {
          return {
            editable,
            nonContainerMessage: Messages.lockPermissions_contentInLockedProject(),
            icon: LockOutlineBaseIcon,
            editabilityDescription: Messages.lockPermissions_capabilityTooltip_lockedProject(),
          };
        } else {
          return {
            editable,
            nonContainerMessage: Messages.lockPermissions_contentInMixedProject(),
          };
        }
    }
  }

  export function getPermissionsEditability(
    authorizables: Set<INamedItemInfo>,
    authorizableType: AuthorizableType,
    sourceAuthorizables: _.Dictionary<ITypedIdWithName>
  ): PermissionsEditability {
    const editable = getEditable(authorizables, authorizableType, sourceAuthorizables);
    return getAllEditabilityData(editable, authorizables, authorizableType, sourceAuthorizables);
  }
}
